(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-lpownv1]';

  const safePlay = (video) => {
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-lpown-gallery]');
    if (!gallery || gallery.dataset.ready === 'true') return;
    gallery.dataset.ready = 'true';

    const track = gallery.querySelector('[data-lpown-gallery-track]');
    const items = [...gallery.querySelectorAll('[data-lpown-gallery-item]')];
    const dotsWrap = gallery.querySelector('[data-lpown-gallery-dots]');
    const previous = gallery.querySelector('[data-lpown-gallery-prev]');
    const next = gallery.querySelector('[data-lpown-gallery-next]');
    let active = Math.min(Math.max(Number(gallery.dataset.startIndex) || 0, 0), Math.max(items.length - 1, 0));
    let scrollTimer;

    if (!items.length) return;

    const dots = items.map((_, index) => {
      if (!dotsWrap) return null;
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `Show theme preview ${index + 1}`);
      button.addEventListener('click', () => setActive(index, true));
      dotsWrap.appendChild(button);
      return button;
    });

    const pauseOtherVideos = (except = null) => {
      items.forEach((item) => {
        const video = item.querySelector('video');
        if (video && video !== except) video.pause();
      });
    };

    const render = () => {
      items.forEach((item, index) => {
        const selected = index === active;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-current', selected ? 'true' : 'false');
        const video = item.querySelector('video');
        if (video && !selected) video.pause();
      });
      dots.forEach((dot, index) => dot?.classList.toggle('is-active', index === active));
    };

    const center = (smooth = true) => {
      const item = items[active];
      if (!track || !item) return;
      track.scrollTo({ left: item.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    };

    function setActive(index, smooth = true) {
      active = (index + items.length) % items.length;
      render();
      center(smooth);
    }

    previous?.addEventListener('click', () => setActive(active - 1));
    next?.addEventListener('click', () => setActive(active + 1));

    items.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (active !== index) {
          setActive(index);
          return;
        }
        const video = item.querySelector('video');
        if (!video) return;
        if (video.paused) {
          pauseOtherVideos(video);
          safePlay(video);
        } else {
          video.pause();
        }
      });
    });

    track?.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const centerPoint = track.scrollLeft + track.clientWidth / 2;
        let nearest = active;
        let distance = Infinity;
        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const delta = Math.abs(itemCenter - centerPoint);
          if (delta < distance) {
            distance = delta;
            nearest = index;
          }
        });
        active = nearest;
        render();
      }, 100);
    }, { passive: true });

    render();
    requestAnimationFrame(() => center(false));
  };

  const initCopy = (root) => {
    const button = root.querySelector('[data-lpown-copy]');
    const password = root.querySelector('[data-lpown-password]');
    const status = root.querySelector('[data-lpown-copy-status]');
    if (!button || !password) return;

    button.addEventListener('click', async () => {
      const value = password.textContent.trim();
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);
      } catch (error) {
        const input = document.createElement('textarea');
        input.value = value;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      const original = button.textContent;
      button.textContent = 'COPIED';
      if (status) status.textContent = 'Password copied. Open the preview and paste it on the password page.';
      window.setTimeout(() => {
        button.textContent = original;
        if (status) status.textContent = '';
      }, 2600);
    });
  };

  const initVariant = (root) => {
    const select = root.querySelector('[data-lpown-variant-select]');
    const input = root.querySelector('[data-lpown-variant-input]');
    const price = root.querySelector('[data-lpown-price]');
    if (!select || !input) return;

    select.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      input.value = select.value;
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
    });
  };

  const parseResponse = async (response) => {
    const type = response.headers.get('content-type') || '';
    if (type.includes('application/json')) return response.json();
    const text = await response.text();
    try { return JSON.parse(text); } catch (_) { return { description: text }; }
  };

  const initProductForm = (root) => {
    const form = root.querySelector('.lpownv1-product-form');
    const status = root.querySelector('[data-lpown-form-status]');
    if (!form || form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      const variantInput = form.querySelector('[name="id"]');
      const variantId = Number(variantInput?.value);

      if (root.dataset.designMode === 'true') {
        if (status) status.textContent = 'Add to cart works in the unpublished storefront preview.';
        return;
      }

      if (!variantId) {
        if (status) {
          status.textContent = 'Select a valid OWN product or variant.';
          status.classList.add('is-error');
        }
        return;
      }

      submitter?.setAttribute('disabled', 'disabled');
      submitter?.setAttribute('aria-busy', 'true');
      if (status) {
        status.textContent = 'Adding OWN the LARP…';
        status.classList.remove('is-error');
      }

      try {
        if (window.LPConversionsCart?.add) {
          await window.LPConversionsCart.add(variantId, 1);
        } else {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          const payload = await parseResponse(response);
          if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this product.');

          const cartResponse = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          const cart = await parseResponse(cartResponse);
          document.dispatchEvent(new CustomEvent('lp:cart:updated', { detail: { cart, action: 'add', source: 'lp-own-product-conversions-v1' } }));

          if (root.dataset.openCart === 'true' && window.LPConversionsCart?.open) {
            window.LPConversionsCart.open();
          } else if (root.dataset.openCart === 'true') {
            window.location.href = `${rootUrl}cart`;
          }
        }

        if (status) status.textContent = 'Added to your cart.';
      } catch (error) {
        if (status) {
          status.textContent = error.message || 'Unable to add this product. Please try again.';
          status.classList.add('is-error');
        }
      } finally {
        submitter?.removeAttribute('disabled');
        submitter?.removeAttribute('aria-busy');
      }
    });
  };

  const initTopSubmitters = (root) => {
    const form = root.querySelector('.lpownv1-product-form');
    root.querySelectorAll('[data-lpown-submit-top]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!form) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.querySelector('[type="submit"]')?.click();
      });
    });
  };

  const initLibrary = (root) => {
    const library = root.querySelector('[data-lpown-library]');
    if (!library) return;
    const filtersWrap = library.querySelector('[data-lpown-filters]');
    const clips = [...library.querySelectorAll('[data-lpown-clip]')];
    if (!filtersWrap || !clips.length) return;

    const categories = [...new Set(clips.map((clip) => clip.dataset.category).filter(Boolean))];
    const labelFor = (handle) => {
      const clip = clips.find((item) => item.dataset.category === handle);
      return clip?.querySelector('p')?.textContent.trim() || handle;
    };

    const renderFilter = (label, category) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.dataset.category = category;
      button.addEventListener('click', () => {
        filtersWrap.querySelectorAll('button').forEach((item) => item.classList.toggle('is-active', item === button));
        clips.forEach((clip) => clip.classList.toggle('is-hidden', category !== 'all' && clip.dataset.category !== category));
      });
      filtersWrap.appendChild(button);
      return button;
    };

    const all = renderFilter('ALL', 'all');
    all.classList.add('is-active');
    categories.forEach((category) => renderFilter(labelFor(category), category));

    library.addEventListener('click', (event) => {
      const play = event.target.closest('[data-lpown-clip-play]');
      if (!play) return;
      const clip = play.closest('[data-lpown-clip]');
      const video = clip?.querySelector('video');
      if (!video) return;
      library.querySelectorAll('video').forEach((item) => { if (item !== video) item.pause(); });
      if (video.paused) {
        safePlay(video);
        play.textContent = 'Ⅱ';
      } else {
        video.pause();
        play.textContent = '▶';
      }
    });
  };

  const initSticky = (root) => {
    const sticky = root.querySelector('[data-lpown-sticky]');
    const top = root.querySelector('[data-lpown-top]');
    if (!sticky || !top || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting && window.innerWidth <= 820);
    }, { threshold: 0.05 });
    observer.observe(top);
  };

  const initReveal = (root) => {
    const items = [...root.querySelectorAll('.lpownv1-reveal')];
    if (!root.classList.contains('lpownv1--animate') || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const init = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    initGallery(root);
    initCopy(root);
    initVariant(root);
    initProductForm(root);
    initTopSubmitters(root);
    initLibrary(root);
    initSticky(root);
    initReveal(root);
  };

  const boot = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
